import React from 'react'
import { Plus, CheckCircle, AlertCircle, Webhook, Package } from 'lucide-react'

export default function BillingTab({ partners, pricingData, webhookConfig, handleAddSeats }) {
  return (
    <div className="space-y-6">
      {/* Pricing Overview */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold mb-2">Team Seat Pricing</h2>
        <p className="text-indigo-100">
          Partners purchase team seats to sync teams to Squadline. Bulk discounts available.
        </p>
      </div>

      {/* Pricing Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {pricingData?.tiers?.map((tier, idx) => (
          <PricingTierCard key={tier.quantity} tier={tier} isPopular={idx === 2} />
        ))}
      </div>

      {/* Partner Seat Balances */}
      <div className="bg-zinc-800 rounded-xl border border-zinc-700">
        <div className="p-4 border-b border-zinc-700">
          <h3 className="font-semibold">Partner Seat Balances</h3>
          <p className="text-sm text-zinc-400">Manage team seats for each integration partner</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Partner</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Available Seats</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Used Seats</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Total Purchased</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-zinc-400">
                    No partners yet
                  </td>
                </tr>
              ) : (
                partners.map((partner) => (
                  <tr key={partner.partner_id} className="hover:bg-zinc-900">
                    <td className="px-6 py-4">
                      <p className="font-medium">{partner.name}</p>
                      <p className="text-sm text-zinc-400">{partner.contact_email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                        (partner.available_seats || 0) > 0 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        <Package className="w-3 h-3" />
                        {partner.available_seats || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-zinc-300">
                      {partner.used_seats || 0}
                    </td>
                    <td className="px-6 py-4 text-zinc-300">
                      {partner.total_seats_purchased || 0}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleAddSeats(partner.partner_id, partner.name)}
                        className="inline-flex items-center gap-1 px-3 py-1 text-sm bg-green-500/100/20 text-green-400 rounded-lg hover:bg-green-500/100/30"
                      >
                        <Plus className="w-3 h-3" />
                        Add Seats
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Webhook Configuration Status */}
      <WebhookConfigSection webhookConfig={webhookConfig} />
    </div>
  )
}

function PricingTierCard({ tier, isPopular }) {
  return (
    <div 
      className={`bg-zinc-800 rounded-xl border-2 p-6 ${
        isPopular ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-zinc-700'
      }`}
    >
      {isPopular && (
        <span className="inline-block bg-green-500/100 text-white text-xs font-bold px-2 py-1 rounded-full mb-3">
          MOST POPULAR
        </span>
      )}
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-3xl font-bold">${tier.price_per_seat_dollars}</span>
        <span className="text-zinc-400">/team</span>
      </div>
      <p className="text-lg font-medium text-white mb-2">{tier.name}</p>
      {tier.discount_percent > 0 && (
        <span className="inline-block bg-green-500/20 text-green-400 text-sm font-medium px-2 py-1 rounded mb-3">
          Save {tier.discount_percent}%
        </span>
      )}
      <div className="border-t border-zinc-700 pt-3 mt-3">
        <p className="text-sm text-zinc-300">
          <span className="font-semibold text-white">{tier.quantity}</span> team seats
        </p>
        <p className="text-lg font-bold text-green-500 mt-1">
          ${tier.total_dollars.toFixed(2)} total
        </p>
      </div>
    </div>
  )
}

function WebhookConfigSection({ webhookConfig }) {
  return (
    <div className="bg-zinc-800 rounded-xl border border-zinc-700 p-6">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <Webhook className="w-5 h-5 text-green-500" />
        Stripe Webhook Configuration
      </h3>
      
      {webhookConfig ? (
        <div className="space-y-4">
          <p className="text-sm text-zinc-300 mb-4">
            Configure these webhooks in your{' '}
            <a 
              href={webhookConfig.stripe_dashboard_url} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-green-500 hover:underline"
            >
              Stripe Dashboard
            </a>
          </p>
          
          {webhookConfig.webhooks?.map((webhook) => (
            <div key={webhook.id} className="border border-zinc-700 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h4 className="font-medium">{webhook.name}</h4>
                  <code className="text-xs bg-zinc-800 px-2 py-1 rounded mt-1 block">
                    {webhook.endpoint_url}
                  </code>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  webhook.secret_configured 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {webhook.secret_configured ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      Configured
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3 h-3" />
                      Not Configured
                    </>
                  )}
                </span>
              </div>
              <div className="mt-2">
                <p className="text-xs text-zinc-400 mb-1">Required events:</p>
                <div className="flex flex-wrap gap-1">
                  {webhook.events.map((event) => (
                    <code key={event} className="text-xs bg-zinc-800 px-1.5 py-0.5 rounded">
                      {event}
                    </code>
                  ))}
                </div>
              </div>
              <p className="text-xs text-zinc-400 mt-2">
                Environment variable: <code className="bg-zinc-800 px-1 rounded">{webhook.env_var}</code>
              </p>
            </div>
          ))}

          <div className="bg-blue-500/10 border border-blue-200 rounded-lg p-4 mt-4">
            <h4 className="font-medium text-blue-900 mb-2">Setup Instructions</h4>
            <ol className="text-sm text-blue-800 space-y-1">
              {webhookConfig.setup_instructions?.map((step, idx) => (
                <li key={idx}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-zinc-400">
          <Webhook className="w-8 h-8 mx-auto mb-2 text-zinc-500" />
          <p>Loading webhook configuration...</p>
        </div>
      )}
    </div>
  )
}
